import { Loader2 } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AnalyticsRange, useActivityTrend, useAnalyticsOverview, usePageClicks } from '../../queries/analytics';

function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

export function OverviewTab({ range }: { range: AnalyticsRange }) {
  const { data, isLoading } = useAnalyticsOverview(range);
  if (isLoading) return <Spinner />;
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="rounded-lg border border-border p-4">
        <p className="text-sm font-medium">Page Views</p>
        <p className="text-xs text-muted-foreground">Total page views in the selected period</p>
        <p className="mt-3 text-3xl font-bold">{data.pageViews.toLocaleString()}</p>
      </div>
      <div className="rounded-lg border border-border p-4">
        <p className="text-sm font-medium">Top Pages</p>
        <p className="text-xs text-muted-foreground">Most visited pages</p>
        <div className="mt-3 space-y-1">
          {data.topPages.length === 0 && <p className="text-sm text-muted-foreground">No data.</p>}
          {data.topPages.map((p) => (
            <div key={p.page} className="flex items-center justify-between text-sm">
              <span>{p.page}</span>
              <span className="font-medium">{p.views.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ActivityTrendTab({ range }: { range: AnalyticsRange }) {
  const { data, isLoading } = useActivityTrend(range);
  if (isLoading) return <Spinner />;
  if (!data) return null;
  return (
    <div className="h-80 rounded-lg border border-border p-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e5e7eb)" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="pageViews" name="Page views" stroke="#3B82F6" dot={false} />
          <Line type="monotone" dataKey="uniqueUsers" name="Unique users" stroke="#10B981" dot={false} />
          <Line type="monotone" dataKey="registrations" name="Registrations" stroke="#8B5CF6" dot={false} />
          <Line type="monotone" dataKey="logins" name="Logins" stroke="#F59E0B" dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Colour ramp for the heatmap cells - light (few clicks) to strong (many).
function heatColor(value: number, max: number): string {
  if (value === 0) return 'transparent';
  const intensity = Math.min(1, value / (max || 1));
  return `rgba(59, 130, 246, ${0.15 + intensity * 0.7})`;
}

export function PageClicksTab({ range }: { range: AnalyticsRange }) {
  const { data, isLoading } = usePageClicks(range);
  if (isLoading) return <Spinner />;
  if (!data) return null;

  const maxHeat = Math.max(1, ...data.heatmapData.map((h) => h.clicks));
  const heatDays = Array.from(new Set(data.heatmapData.map((h) => h.date))).sort();
  const heatByKey = new Map(data.heatmapData.map((h) => [`${h.date}-${h.hour}`, h.clicks]));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-border p-4">
          <p className="mb-3 text-sm font-medium">Top Pages by Clicks</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.topPages} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e5e7eb)" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="clicks" name="Clicks" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-lg border border-border p-4">
          <p className="mb-3 text-sm font-medium">Racing Track Clicks</p>
          <div className="space-y-1">
            {data.trackClicks.length === 0 && <p className="text-sm text-muted-foreground">No track clicks.</p>}
            {data.trackClicks.map((t) => (
              <div key={t.name} className="flex items-center justify-between text-sm">
                <span>{t.name}</span>
                <span className="font-medium">{t.clicks.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border p-4">
        <p className="mb-3 text-sm font-medium">Hourly Activity (last 7 days, Brisbane time)</p>
        <table className="text-xs">
          <thead>
            <tr>
              <th className="pr-2 text-left font-normal text-muted-foreground">Day</th>
              {Array.from({ length: 24 }, (_, h) => (
                <th key={h} className="w-6 text-center font-normal text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {heatDays.map((day) => (
              <tr key={day}>
                <td className="pr-2 text-muted-foreground">{day.slice(5)}</td>
                {Array.from({ length: 24 }, (_, h) => {
                  const clicks = heatByKey.get(`${day}-${h}`) ?? 0;
                  return (
                    <td key={h} className="p-0.5">
                      <div
                        className="h-5 w-5 rounded-sm"
                        style={{ backgroundColor: heatColor(clicks, maxHeat) }}
                        title={`${day} ${h}:00 — ${clicks} clicks`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FunnelTab({ range }: { range: AnalyticsRange }) {
  const { data, isLoading } = useAnalyticsOverview(range);
  if (isLoading) return <Spinner />;
  if (!data) return null;
  const f = data.registrationFunnel;
  const stages = [
    { label: 'Registration page views', value: f.pageViews },
    { label: 'Form started', value: f.formStarted },
    { label: 'Form submitted', value: f.formSubmitted },
    { label: 'Registration success', value: f.registrationSuccess },
  ];
  const max = Math.max(1, ...stages.map((s) => s.value));
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="mb-4 flex items-baseline justify-between">
        <p className="text-sm font-medium">Registration Funnel</p>
        <p className="text-sm text-muted-foreground">
          Conversion rate: <span className="font-semibold text-foreground">{f.conversionRate.toFixed(1)}%</span>
        </p>
      </div>
      <div className="space-y-2">
        {stages.map((s) => (
          <div key={s.label}>
            <div className="flex items-center justify-between text-sm">
              <span>{s.label}</span>
              <span className="font-medium">{s.value.toLocaleString()}</span>
            </div>
            <div className="mt-1 h-2 rounded-full bg-muted">
              <div className="h-2 rounded-full bg-blue-500" style={{ width: `${(s.value / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
